// Consumer-facing display of a market price band. Pure arithmetic on top of
// the band /api/pris-estimat returns — no own pricing model, no DB, no
// hardcoded prices.
//
// Everything about "what does the job cost" lives in features/pricing/shared/estimate.ts
// (hours) + features/pricing/server/price-band.ts (hourly rate → band). This file only translates
// a per-visit band into the numbers a customer asks about: per month, hourly
// rate, tax deduction.

import type { MarketPriceBand } from "@/features/pricing/shared/price-band";
import {
  DOEDSBO_FRADRAG_NOTE,
  FLYTTERENGOERING_FRADRAG_NOTE,
} from "@/features/pricing/shared/servicefradrag";
import { formatKrRange } from "@/shared/format/money";

export type OereRange = { minOere: number; maxOere: number };

export type PriceView = {
  perVisit: OereRange;
  // Only for recurring agreements — a one-off job has no monthly price.
  perMonth: OereRange | null;
  perTimeOere: number;
  estimatedHours: number;
  // Annual tax savings via the servicefradrag (BoligJobordningen).
  servicefradragYearlyOere: OereRange | null;
  primaryUnit: "visit" | "month";
  notes: string[];
  exclVat: boolean;
};

// Servicefradrag (BoligJobordningen) 2026. Two separate numbers that must never
// be conflated: you DEDUCT the labor cost, up to 18,300 kr per person per year,
// and the deduction's TAX VALUE is ~26%. A fully used cap is therefore ~4,758 kr
// off your tax bill, not 18,300 kr. The cap covers all serviceydelser combined
// (cleaning, window washing, gardening, childcare). There is no separate,
// lower cap for cleaning alone.
// Source: skat.dk, Fradrag for serviceydelser + Juridisk Vejledning C.A.4.3.6.
// Verified 11 August 2026. The 2025 cap was 17,500 kr.
//
// The cap sits on the BASE, not on the saving. Until 2026-08-11 this read
// max 6,600 kr (the 2023 rate) applied to the saving, which was wrong at both
// ends.
//
// We compute on the whole price, not just the labor share. Same simplification
// as before, and it stays on the conservative side of the cap.
const SERVICEFRADRAG_RATE = 0.26;
const SERVICEFRADRAG_MAX_BASE_PR_PERSON_OERE = 1_830_000;

const VISITS_PER_MONTH: Record<string, number> = {
  dagligt: 21,
  "2-3x-ugentligt": 10,
  ugentligt: 4,
  "hver-14-dag": 2,
  maanedligt: 1,
  "efter-behov": 1,
};

// One-off subtypes where the servicefradrag never applies. Only dødsbo: nobody
// has fast bopæl in the home, so LL § 8 V, stk. 2, nr. 2 can't be met by anyone
// paying the bill.
//
// Flytterengøring is deliberately absent: the deduction turns on where the
// customer lives on the day, not on what the job is called (see
// lib/servicefradrag.ts for the statute). Fraflytningsrengøring is the common
// case and it qualifies, so we show the saving and attach the condition.
const NO_SERVICEFRADRAG_SUBTYPES = new Set(["doedsbo"]);

// One-off subtypes where the deduction depends on something we can't see from
// the form, so the number ships with its condition stated.
const CONDITIONAL_SERVICEFRADRAG_SUBTYPES = new Set(["flytterengoering"]);

export function scale(range: OereRange, factor: number): OereRange {
  return {
    minOere: Math.round(range.minOere * factor),
    maxOere: Math.round(range.maxOere * factor),
  };
}

// Annual tax saving: the cap trims the base first, and only then does the tax
// value apply.
export function servicefradragSaving(yearly: OereRange): OereRange {
  const taxSaved = (spentOere: number) =>
    Math.round(Math.min(spentOere, SERVICEFRADRAG_MAX_BASE_PR_PERSON_OERE) * SERVICEFRADRAG_RATE);
  return {
    minOere: taxSaved(yearly.minOere),
    maxOere: taxSaved(yearly.maxOere),
  };
}

export function perMonthRange(perVisit: OereRange, frequency: string | null): OereRange | null {
  const visits = frequency ? VISITS_PER_MONTH[frequency] : undefined;
  return visits ? scale(perVisit, visits) : null;
}

export function toPriceView(
  band: MarketPriceBand,
  input: {
    clientType: "privat" | "erhverv";
    recurring: boolean;
    frequency: string | null;
    engangsSubtype: string | null;
  },
): PriceView {
  const perVisit: OereRange = { minOere: band.minOere, maxOere: band.maxOere };
  const perMonth = input.recurring ? perMonthRange(perVisit, input.frequency) : null;
  const notes: string[] = [];

  // Danish convention: privat is priced incl. VAT, erhverv excl. Note the
  // partner form doesn't actually ask what `hourlyRateOere` is quoted as, so
  // this is an assumption, not data.
  const exclVat = input.clientType === "erhverv";

  let servicefradragYearlyOere: OereRange | null = null;
  if (input.clientType === "privat") {
    if (perMonth) {
      servicefradragYearlyOere = servicefradragSaving(scale(perMonth, 12));
    } else if (!NO_SERVICEFRADRAG_SUBTYPES.has(input.engangsSubtype ?? "")) {
      servicefradragYearlyOere = servicefradragSaving(perVisit);
      if (CONDITIONAL_SERVICEFRADRAG_SUBTYPES.has(input.engangsSubtype ?? "")) {
        notes.push(FLYTTERENGOERING_FRADRAG_NOTE);
      }
    } else {
      notes.push(DOEDSBO_FRADRAG_NOTE);
    }
  }

  return {
    perVisit,
    perMonth,
    perTimeOere: band.hourlyRateOere,
    estimatedHours: band.estimatedHours,
    servicefradragYearlyOere,
    primaryUnit: perMonth ? "month" : "visit",
    notes,
    exclVat,
  };
}

// A band the estimator could not produce renders as an em dash rather than
// "0 kr.", which would read as a quote. Everything else is plain money
// formatting from @/shared/format/money.
export function formatOereRange(range: OereRange | null): string {
  if (!range) return "—";
  return formatKrRange(range.minOere, range.maxOere);
}

export function formatHours(hours: number): string {
  const rounded = Math.round(hours * 2) / 2;
  return `${rounded.toString().replace(".", ",")} timer`;
}

// A recurring agreement is priced per visit; a one-off job has a single total,
// so "pr. gang" would promise a repeat that was never ordered.
export function priceUnitLabel(recurring: boolean): string {
  return recurring ? "pr. gang" : "i alt";
}
